/**
 * Every user-visible string of the UI (UX-WORD-02). Plain constants and small functions, no DOM, so the worker's report
 * builders and the word test (T-UNIT-WORD-01) can read them. While the model is uncalibrated none of these may contain a
 * word matched by `BANNED_WORDS` (UX-WORD-01); the engine's own strings (headline, caveat, notes) are shown verbatim and
 * are not defined here. Voice: plain, short, specific; numbers with their units; no exclamation marks (UX-SPEC §12.8).
 */

import { CHANNEL_IDS } from '../engine/channels/types.ts';
import { TRANSLATION_SCOPE } from '../engine/ensemble/wording.ts';

/** The number of channels in the registry (35 before T2, 37 with TR-01 and TR-02). */
const N = CHANNEL_IDS.length;

export const APP = {
	name: 'Provenance',
	tagline: 'Stylometric evidence of machine authorship and machine translation',
	skip: 'Skip to main view',
	/** Shown beside the name; no calibrated model ships in v1 (DESIGN PD-7). */
	prototype: (version: string) => `Prototype v${version} · uncalibrated`,
} as const;

export const NAV = {
	overview: 'Overview',
	reader: 'Reader',
	channels: 'Channels',
	hotspots: 'Hotspots',
	diagnostics: 'Diagnostics',
	bench: 'Bench',
	views: 'Views',
	more: 'More',
} as const;

export const HEADER = {
	newAnalysis: 'New analysis',
	export: 'Export',
	words: (n: string) => `${n} words`,
	preparing: 'Preparing…',
	exportFailed: (what: string) => `The export could not be built: ${what}`,
	copyHeadline: 'Copy headline',
	copied: 'Copied',
} as const;

export const EXPORT_ITEMS = {
	json: 'Segment report (JSON)',
	md: 'Report (Markdown)',
	txt: 'Report (plain text)',
	html: 'Report (HTML)',
	docx: 'Report (Word)',
} as const;

export const INTAKE = {
	title: 'Analyse a document',
	help: 'Designed for manuscripts of 200,000 to 500,000 words. Shorter texts are analysed too; channels without enough text abstain and say why.',
	tabs: { document: 'Document', paste: 'Paste', url: 'URL' },
	tablist: 'Where the text comes from',
	dropZone: 'Drop a manuscript here or choose a file',
	chooseFile: 'Choose a file',
	accepted: 'Accepted: .txt .md .markdown .html .htm .xhtml .docx .pdf',
	chosen: (name: string, size: string, format: string) => `${name} · ${size} · ${format}`,
	changeFile: 'Choose another file',
	pasteLabel: 'Paste the text here',
	pasteWords: (n: string) => `${n} words`,
	pasteHint: 'Pasting suits shorter texts. Manuscripts go in the Document tab.',
	loadSample: 'Load AI-flavoured sample',
	urlLabel: 'Address of the page or file',
	urlPlaceholder: 'https://',
	run: 'Run analysis',
	formatNames: {
		txt: 'plain text',
		md: 'Markdown',
		html: 'HTML',
		docx: 'Word document',
		pdf: 'PDF',
		paste: 'pasted text',
		url: 'web address',
		unknown: 'unrecognised format',
	},
	recent: 'Recent analyses',
	recentEmpty: 'Nothing stored yet.',
	open: 'Open',
	remove: 'Remove',
	earlierEngine: 'made with an earlier engine',
	runAgainToOpen: 'Run again',
	recentLine: (words: string, index: string, date: string) =>
		`${words} words · index ${index} · ${date}`,
	removeTitle: 'Remove this stored analysis?',
	removeBody: (name: string) =>
		`${name} will be removed from this browser. The original file is not affected.`,
	removeConfirm: 'Remove',
	cancel: 'Cancel',
} as const;

export const STATE_TEXT = {
	reading: (name: string) => `Reading ${name}…`,
	fetching: (host: string) => `Fetching ${host}…`,
	parsing: 'Reading the document',
	intakeChecked: (words: string, format: string) => `${words} words · ${format}`,
	analysing: 'Analysing the document',
	rendering: 'Preparing the overview',
	restoring: (name: string) => `Restoring ${name}…`,
	interrupted: (name: string) => `The analysis of ${name} did not finish.`,
	runAgain: 'Run again',
	discard: 'Discard',
	cancelled: 'Analysis cancelled.',
	tryAgain: 'Try again',
	back: 'Back to the intake',
	restoredOn: (when: string) => `Restored from ${when}.`,
	cancel: 'Cancel',
	elapsed: (t: string) => `Elapsed ${t}`,
	waitingFor: 'Working…',
	progressLabel: 'Analysis progress',
	stage: {
		read: 'Reading the document',
		document: 'Building the document model',
		channels: `Measuring ${N} channels`,
		segments: 'Segmenting and testing for hotspots',
		ensemble: 'Aggregating the index and its intervals',
		finalise: 'Preparing the evidence map',
	},
	page: (page: string, pages: string) => `page ${page} of ${pages}`,
	stageAnnounce: (label: string) => `Stage: ${label}`,
	overLimitTitle: 'This document is over the word limit',
	chooseAnother: 'Choose another file',
	editUrl: 'Edit the address',
	downloadInstead: 'Download it and use the Document tab',
	storageNotice: 'This analysis could not be saved for restoring after a reload.',
	errorLabel: 'Error:',
} as const;

/**
 * Working feedback (DESIGN UD-11, UX-SPEC §15a): the label of every operation that can take about a second or more, and
 * the rounded time estimate shown beside it. Plain words, no promise: an estimate says "about".
 */
export const WORKING = {
	statusLabel: 'Working',
	/** The status bar item's button that opens the full status (UD-20). */
	details: 'Details',
	/** The tooltip of a status line that opens the full status when clicked. */
	openHint: 'Click for what is happening behind the scenes',
	elapsed: (t: string) => `Elapsed ${t}`,
	/** The estimate; the rounding steps are in `src/web/estimate.ts`. */
	left: {
		fewSeconds: 'a few seconds left',
		seconds: (n: string) => `about ${n} seconds left`,
		minute: 'about 1 minute left',
		minutes: (n: string) => `about ${n} minutes left`,
		hour: 'about 1 hour left',
		hours: (n: string) => `about ${n} hours left`,
	},
	/** Shown instead of a number when a time estimate would be a guess. */
	longerThanExpected: 'This is taking longer than expected.',
	formats: { json: 'JSON', md: 'Markdown', txt: 'plain text', html: 'HTML', docx: 'Word' },
	ops: {
		reading: (name: string) => `Reading ${name}`,
		fetching: (host: string) => `Fetching ${host}`,
		restoring: (name: string) => `Restoring ${name}`,
		rendering: 'Preparing the overview',
		export: (format: string) => `Building the ${format} export`,
		rewriteExport: (format: string) => `Building the ${format} of the rewritten text`,
		estimating: 'Planning the rewrite and its cost',
		localPass: 'Running the local pass and measuring its effect',
		rewriting: 'Rewriting',
		brief: 'Reading the whole document for its repeated phrases, names and voices',
		resuming: 'Preparing to resume the job',
		economyWaiting: (round: string) => `Economy round ${round}: waiting for the batch`,
		economySubmitting: (round: string) => `Economy round ${round}: sending the requests`,
		economyApplying: (round: string) => `Economy round ${round}: checking the answers`,
		counting: 'Counting words',
	},
	/** The local pass's stages, shown as the indicator's second line (UD-12). */
	localStages: {
		analysing: 'Analysing the text',
		transforming: 'Applying the transforms',
		measuring: 'Measuring the effect',
	},
	batchCounts: (done: string, total: string) => `${done} of ${total} requests answered`,
	batchTypical: 'Batches usually finish within an hour; the longest allowed is 24 hours.',
	stretches: (done: string, total: string) => `${done} of ${total} stretches`,
	/** The rewrite line on the parallel path (bench v3, UD-20): rounds in waves, answers applied in order. */
	round: {
		starting: 'Round 1: sending the first requests',
		answers: (wave: string, done: string, total: string) =>
			`Round ${wave}: ${done} of ${total} answers in`,
		finalPass: (done: string, total: string) => `Final pass: ${done} of ${total} answers in`,
		/** The passes after the rounds (the audit's F-1.8): no request goes out; no time estimate is shown. */
		finalChecks: {
			checkpoint: 'Final checks: the whole-document check',
			analysis: (done: string, total: string) =>
				total === '1'
					? 'Final checks: analysing the rewritten text'
					: `Final checks: analysing the rewritten text (${done === '0' ? '1' : '2'} of 2)`,
			lenient: (done: string, total: string) =>
				`Final checks: re-measuring the failed passages, ${done} of ${total}`,
		},
		inFlight: (n: string) => (n === '1' ? '1 request in flight' : `${n} requests in flight`),
	},
} as const;

export const ERRORS = {
	tooBig: (size: string) => `This file is ${size}, over the 100 MB intake limit.`,
	fetch: {
		bad_url: 'That address is not a valid http or https URL.',
		blocked_destination: 'That address points to a private or blocked network location.',
		too_many_redirects: 'The page redirected too many times.',
		too_large: 'The file at that address is over the fetch size limit.',
		timeout: 'The site did not answer in time.',
		unsupported_type: 'That address returned a file type Provenance cannot read.',
		upstream_status: 'The site answered with an error.',
		rate_limited: (seconds: number) => `Too many fetches. Try again in ${seconds} seconds.`,
		unreachable: 'The fetch service could not be reached.',
	},
	crashed:
		'The browser stopped the analysis. This usually means the computer ran out of memory. Close other tabs and applications and run it again; documents of this size need a computer with at least 4 GB of memory.',
	internal: (message: string) => `The analysis failed: ${message}.`,
	diagnosticsId: (id: string) => `Diagnostics id: ${id}`,
	emptyInput: 'There is nothing to analyse yet.',
	fileRead: 'The file could not be read.',
} as const;

/**
 * The fixed caveat beside the machine-authorship index (DESIGN question Q1 of the C5 handoff, option B). Wording only; no
 * score moves. The count and n are the measured figures of docs/LIMITATIONS.md (51 of 129 human political speeches).
 */
export const SPEECH_CAVEAT = {
	lead: 'Speeches, transcripts and other spoken or heavily edited text are often flagged: 51 of 129 human political speeches were, in our measurements. See',
	link: 'Limitations',
	short: 'Spoken or heavily edited text is often flagged. See',
	/** The plain-text form for exports and the CLI. */
	plain:
		'Speeches, transcripts and other spoken or heavily edited text are often flagged: 51 of 129 human political speeches were, in our measurements. See Limitations.',
} as const;

export const OVERVIEW = {
	title: 'Overview',
	indexLabel: 'Machine-authorship index (uncalibrated)',
	intervalLabel: '90% interval',
	coverageLabel: 'Evidence coverage',
	coverageHelp:
		'How much channel evidence stands behind the index (0.10–1.00), from the text length and the channels’ data weights.',
	translationTitle: 'Translation axis',
	translationLink: 'Open MT-01',
	translationChannels: 'Translation channels',
	authorshipTitle: 'Authorship axis',
	calibratedHint:
		'An index on a 0–100 scale, calibrated against the reference corpus at the reference prior. It is not a percentage of the document’s text. The dial reads it against the reference corpus, with its conditions; the seed-rules composite below it is not calibrated.',
	/** Equal to `INTERVAL_MEANING` in the engine's wording (a unit test holds the two together). */
	intervalMeaning:
		"The text-sampling interval shows how much the index moves when the document's chapters, scenes or paragraphs are resampled; it does not include the uncertainty of the calibration itself. On the reference corpus it understates the variation between independent samples of the same text (model card), so it never decides the status.",
	patternsTitle: 'Provenance patterns',
	patternsNone: 'No provenance pattern indicators fired.',
	patternLead: 'Indicator (a rule output with the evidence that triggered it):',
	assistFloor: 'The index is floored by this rule.',
	noticesTitle: 'Notices',
	withheld: (ids: string) =>
		`Hotspot flags withheld for ${ids} until the channel priors are replaced by corpus values.`,
	lengthDependent: (ids: string) =>
		`${ids} are length-dependent until the channel priors are replaced by corpus values.`,
	abstained: (n: number) => (n === 1 ? '1 channel abstained' : `${n} channels abstained`),
	uncalibrated: (id: string) => `Uncalibrated model: ${id}`,
	/**
	 * Intake hygiene notice (ENGINE-SPEC §2.1 N7, N8): counts and rates arrive formatted; a zero count drops its clause.
	 * Evidence about the text only, never a channel score.
	 */
	hygiene: (
		invisible: string,
		lookalikes: string,
		invisibleRate: string,
		lookalikeRate: string
	) => {
		const parts: string[] = [];
		const rates: string[] = [];
		if (invisible !== '0') {
			parts.push(`${invisible} invisible ${invisible === '1' ? 'character' : 'characters'}`);
			rates.push(invisibleRate);
		}
		if (lookalikes !== '0') {
			parts.push(`${lookalikes} look-alike ${lookalikes === '1' ? 'letter' : 'letters'}`);
			rates.push(lookalikeRate);
		}
		const single = parts.length === 1 && (invisible === '1' || lookalikes === '1');
		return `Text hygiene: ${parts.join(' and ')} ${single ? 'was' : 'were'} normalised before analysis (${rates.join(' and ')} per 1,000 characters).`;
	},
	reason: 'Reason',
	seeDiagnostics: 'See Diagnostics',
	stripTitle: 'Authorship index along the document',
	stripCaption:
		'Index per section that shares heuristic characteristics (joint segments). Each section is aggregated on its own text, so sections need not average to the document index.',
	chaptersTitle: 'Chapters',
	chaptersCaption: 'The index of each natural unit of the document, aggregated on its own text.',
	strongestTitle: 'Channels by evidence strength',
	strongestCaption:
		'The eight highest-scoring channels. This is a shortcut into the channel list, not a ranking of importance.',
	allChannels: `All ${N} channels`,
	hotspotWord: 'hotspot',
	elevatedWord: 'elevated',
	hotspotDef:
		'hotspot: significantly above both the human reference and the document’s own background',
	elevatedDef: 'elevated: significantly above the human reference',
	documentLevelOnly: 'document level only',
} as const;

export const STRIP = {
	bySection: 'By section',
	byChapter: 'By chapter',
	markers: 'Hotspot markers',
	showTable: 'Show as table',
	hideTable: 'Hide table',
	axis0: '0',
	axis100: '100',
	name: (n: number, lo: string, hi: string) =>
		`Authorship index by section: ${n} ${n === 1 ? 'section' : 'sections'}, from ${lo} to ${hi} on a 0–100 scale`,
	nameChapters: (n: number) => `Authorship index by chapter: ${n} chapters on a 0–100 scale`,
	tip: (
		title: string,
		words: string,
		index: string,
		interval: string,
		method: string,
		hot: string
	) => `${title} · ${words} · index ${index} (90% interval ${interval}, ${method}) · ${hot}`,
	hotCount: (n: number) => (n === 1 ? '1 hotspot entry' : `${n} hotspot entries`),
	tableCaption: 'Sections and chapters of the strip',
	windowLabel: 'Visible in the reader',
	section: (n: number) => `Section ${n}`,
	chapter: (n: number) => `Chapter ${n}`,
} as const;

export const TABLE = {
	position: 'Position',
	number: '#',
	title: 'Title',
	words: 'Words',
	index: 'Index',
	interval: '90% interval',
	method: 'Interval method',
	hotspots: 'Hotspot entries',
	sortBy: 'Sort by',
	open: 'Open in the reader',
	none: '—',
} as const;

export const CHANNELS = {
	title: 'Channels',
	intro: `All ${N} channels, reported separately. The score is evidence strength on a 0–100 scale; the interval is 90%; κ is the data weight behind the score.`,
	translationGroup: 'Translation axis',
	authorshipGroup: 'Authorship axis',
	cols: {
		focus: 'Focus',
		id: 'ID',
		name: 'Name',
		family: 'Family',
		score: 'Evidence strength',
		interval: '90% interval',
		kappa: 'Data weight κ',
		deviation: 'Deviation from the reference',
		hotspots: 'Hotspot entries',
		spans: 'Evidence spans',
		notes: 'Notes',
	},
	group: 'Group by',
	groupFamily: 'Family',
	groupNone: 'None',
	sort: 'Sort by',
	sortScore: 'Evidence strength',
	sortDeviation: 'Deviation',
	sortKappa: 'Data weight',
	sortHotspots: 'Hotspot entries',
	sortId: 'ID',
	littleEvidence: 'little evidence',
	abstained: (reason: string) => `Abstained: ${reason}`,
	flagsWithheld: 'Flags withheld',
	lengthDependent:
		'length-dependent until the channel priors are replaced by corpus values (DESIGN ED-14)',
	focusCap: 'At most 3 channels can be focused in the reader; the oldest choice was replaced.',
	openReader: 'Open the reader',
	focusFor: (id: string) => `Focus ${id} in the reader`,
	cards: 'Channels as cards',
	doc: {
		deviationUnit: 'deviation from the reference',
	},
} as const;

export const DETAIL = {
	back: 'All channels',
	previous: 'Previous channel',
	next: 'Next channel',
	family: 'Family',
	axis: 'Axis',
	axisAuthorship: 'authorship',
	axisTranslation: 'translation',
	score: 'Evidence strength',
	kappa: 'Data weight κ',
	deviation: 'Deviation from the reference',
	contribution: 'Contribution to the index sum L',
	measured: (raw: string, unit: string, events: string, exposure: string, exposureUnit: string) =>
		`raw ${raw} ${unit} (${events} weighted events over ${exposure} ${exposureUnit})`,
	withheldBanner: (reason: string) => `Hotspot flags withheld. ${reason}`,
	profileTitle: 'Along the document',
	profileCaption:
		'The per-paragraph rate smoothed over 21 paragraphs, in the channel’s own units, with the channel’s segments as steps. The dashed line is the human reference (prior).',
	referenceLine: 'human reference (prior)',
	noProfile:
		'This channel has no per-paragraph series to draw; see the segment and chapter tables.',
	segmentsTitle: 'Segments',
	unitsTitle: 'Chapters',
	summaryTitle: 'Evidence summary',
	featuresTitle: 'Features',
	spansTitle: 'Evidence spans',
	showInReader: 'Show in reader',
	noSpans: 'No evidence spans for this channel.',
	cols: {
		range: 'Range',
		estimate: 'Estimate',
		score: 'Score',
		flag: 'Flag',
		pRef: 'p (reference)',
		pBg: 'p (background)',
		ratioRef: 'Rate ratio (reference)',
		ratioBg: 'Rate ratio (background)',
		unit: 'Chapter',
		elevated: 'Elevated',
		feature: 'Feature',
		count: 'Spans',
		value: 'Value',
	},
	flagHotspot: 'hotspot',
	flagElevated: 'elevated',
	flagNone: '—',
	flagWithheld: 'withheld',
	yes: 'yes',
	no: 'no',
	paragraphs: (a: string, b: string) => `paragraphs ${a}–${b}`,
	notFound: (id: string) => `There is no channel ${id}.`,
	profileName: (id: string) => `${id} rate along the document`,
	showTable: 'Show as table',
	hideTable: 'Hide table',
} as const;

/**
 * The translation group (T2, DESIGN CD-37, CD-48): what MT-01, TR-01 and TR-02 measure, in plain words. Additive help only:
 * nothing here changes a number or a row. The figures come from `data/oof/t2-claims.json` (model `t2-gam-4ec263a0`).
 */
export const TRANSLATION = {
	/** The scope sentence of the axis (equal to the engine's `TRANSLATION_SCOPE`). */
	scope: TRANSLATION_SCOPE,
	/** Above the channel list, beside the translation group. */
	groupNote:
		'The translation group is evidence, not a result: machine translation is not established by this tool. MT-01 has no demonstrated discriminative value; TR-01 and TR-02 compare word choices with human translators and writers. A single word is never evidence, the rate over many words is.',
	/** On the overview, under the translation headline. */
	axisNote:
		'The translation index is evidence, not a result. Open the translation group in the channel list for what each channel measures.',
	/** The label MT-01 keeps (protocol §5.5: its interval includes 0.5 in every length group). */
	noValue: 'no demonstrated discriminative value',
	explainTitle: 'What this channel measures',
	measuresLabel: 'Measures',
	whyLabel: 'Why it matters',
	cautionLabel: 'How to read it',
	'MT-01': {
		measures:
			'Pipeline residue of machine translation per 1,000 words: sentences with a dropped subject, repeated words (“X and X”), mixed scripts, unbalanced quotes and spacing before punctuation.',
		why: 'It is kept visible as part of the translation axis.',
		caution:
			'In the reference corpus its interval included 0.5 in every length group, so it has no demonstrated discriminative value and carries no weight in the translation model.',
	},
	'TR-01': {
		measures:
			'Word and two-word choices that Claude’s translations into English use more often than human translators and writers do, as a weighted rate per 1,000 words.',
		why: 'It is the machine-leaning half of the lexical evidence: a high rate over a long stretch marks a passage worth reading as a human would.',
		caution:
			'A single word is not evidence; the rate over many words is. Only the strongest word choices are marked in the reader, at most five per paragraph. Institutional and legal translations, such as Korean statutes, can read as machine-leaning: in the reference data the translation model flagged 4 of 38 short human translations from Korean (one-sided 95% upper bound 22.6%).',
	},
	'TR-02': {
		measures:
			'Word and two-word choices that Claude’s translations into English use less often than human translators and writers do, as a weighted rate per 1,000 words.',
		why: 'It is the human-leaning half of the lexical evidence: a low rate here, over a long stretch, points the machine-leaning way.',
		caution:
			'A single word is not evidence; the rate over many words is. Only the strongest word choices are marked in the reader, at most five per paragraph. Institutional and legal translations, such as Korean statutes, can read as machine-leaning: in the reference data the translation model flagged 4 of 38 short human translations from Korean (one-sided 95% upper bound 22.6%).',
	},
	/** The hover text of a channel name in the list: `measures`, then the scope. */
	tip: (measures: string) => `${measures} Measured against Claude Sonnet 5.5 translations only.`,
	/** The hero on the overview when the calibrated translation index is shown (only when gate G4 passed). */
	calibratedHint:
		'An index on a 0–100 scale from the translation model, calibrated against the reference corpus. It is not a percentage. Claude Sonnet 5.5 translations into English are the only system measured.',
} as const;

export const HOTSPOTS = {
	title: 'Hotspots',
	counts: (total: string, parts: string) => `${total} entries: ${parts}.`,
	part: {
		hotspotSegment: (n: string) => `${n} hotspot ${n === '1' ? 'segment' : 'segments'}`,
		elevatedSegment: (n: string) => `${n} elevated ${n === '1' ? 'segment' : 'segments'}`,
		elevatedUnit: (n: string) => `${n} elevated ${n === '1' ? 'chapter' : 'chapters'}`,
		elevatedSpike: (n: string) => `${n} elevated single ${n === '1' ? 'paragraph' : 'paragraphs'}`,
		hotspotUnit: (n: string) => `${n} hotspot ${n === '1' ? 'chapter' : 'chapters'}`,
		hotspotSpike: (n: string) => `${n} hotspot single ${n === '1' ? 'paragraph' : 'paragraphs'}`,
	},
	notListed: (ids: string, reason: string) => `Not listed: ${ids}. Reason: ${reason}.`,
	filters: 'Filters',
	channel: 'Channel',
	kind: 'Kind',
	flag: 'Flag',
	chapter: 'Chapter',
	all: 'All',
	kindSegment: 'segment',
	kindSpike: 'single paragraph',
	kindUnit: 'chapter',
	clear: 'Clear filters',
	shown: (n: string) => `${n} shown`,
	groupByChapter: 'Group by chapter',
	cols: {
		channel: 'Channel',
		kind: 'Kind',
		flag: 'Flag',
		location: 'Location',
		score: 'Score',
		ratioRef: 'vs reference',
		ratioBg: 'vs background',
		p: 'p',
	},
	open: 'Open in the reader',
	noEntries: 'No entries match these filters.',
	noChapter: 'Before the first chapter',
	chapterCount: (n: string) => `${n} entries`,
	rowLabel: (channel: string, where: string) => `${channel}, ${where}`,
} as const;

export const DIAG = {
	title: 'Diagnostics',
	copy: 'Copy diagnostics',
	copied: 'Copied',
	wall: 'Wall time from Run to the first overview paint',
} as const;

export const FOOT = {
	caveatLabel: 'Caveat',
} as const;

export const A11Y = {
	loading: 'Loading',
	viewNav: 'Views',
	bottomNav: 'Views',
	closeMenu: 'Close menu',
	menu: 'Menu',
} as const;
